import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api, currency, daysUntil, computeMatch } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowRight, Clock, Sparkles, TrendingUp, Trophy, FileText, Zap } from "lucide-react";

function StatCard({ label, value, icon: Icon, sub, testId }) {
  return (
    <div className="gf-card p-6" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        <div className="h-9 w-9 rounded-md bg-neutral-100 flex items-center justify-center text-neutral-700"><Icon size={16} /></div>
      </div>
      <div className="mt-3 font-display text-3xl font-bold tracking-tight text-neutral-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-neutral-500">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ total_applications: 0, documents_generated: 0, potential_funding: 0, funding_won: 0 });
  const [featured, setFeatured] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/api/applications/stats"),
      api.get("/api/grants/featured"),
      api.get("/api/applications/"),
    ]).then(([s, f, a]) => {
      setStats(s.data);
      setFeatured(f.data);
      setApps(a.data);
    }).finally(() => setLoading(false));
  }, []);

  const upcoming = apps
    .filter((a) => a.status !== "awarded" && a.status !== "rejected")
    .sort((a, b) => new Date(a.grant_deadline) - new Date(b.grant_deadline))
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div>
            <div className="overline mb-2">DASHBOARD</div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight" data-testid="dashboard-title">
              Welcome back, {user?.name?.split(" ")[0] || "friend"}.
            </h1>
            <p className="text-neutral-600 mt-2">Your grant pipeline at a glance.</p>
          </div>
          {user?.subscription_status !== "active" && (
            <Link to="/pricing" className="btn-accent" data-testid="upgrade-cta">
              <Sparkles size={16} /> {user?.subscription_status === "trialing" ? "Upgrade to Pro" : "Unlock AI docs — $100/mo"}
            </Link>
          )}
        </div>

        {user?.subscription_status === "trialing" && user?.trial_ends_at && (() => {
          const days = Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / (1000*60*60*24)));
          const urgent = days <= 3;
          return (
            <div className={`mb-8 rounded-2xl border p-5 flex items-center justify-between gap-4 flex-wrap ${urgent ? "bg-red-50 border-red-200" : "bg-blue-50 border-blue-200"}`} data-testid="trial-banner">
              <div>
                <div className="font-display text-lg font-semibold">
                  {days > 0 ? `${days} day${days === 1 ? "" : "s"} left on your free trial` : "Your trial ends today"}
                </div>
                <div className="text-sm text-neutral-700 mt-0.5">Everything Pro is unlocked. No card required until you decide.</div>
              </div>
              <Link to="/pricing" className={urgent ? "btn-accent" : "btn-primary"} data-testid="trial-upgrade">
                <Sparkles size={16} /> Upgrade now
              </Link>
            </div>
          );
        })()}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Applications" value={stats.total_applications} icon={FileText} sub="in your pipeline" testId="stat-apps" />
          <StatCard label="Docs generated" value={stats.documents_generated} icon={Zap} sub="AI-drafted" testId="stat-docs" />
          <StatCard label="Potential funding" value={currency(stats.potential_funding)} icon={TrendingUp} sub="across drafts" testId="stat-potential" />
          <StatCard label="Funding won" value={currency(stats.funding_won)} icon={Trophy} sub="awarded to date" testId="stat-won" />
        </div>

        <div className="mt-10 grid lg:grid-cols-3 gap-6">
          {/* Upcoming deadlines */}
          <div className="lg:col-span-2 gf-card p-6" data-testid="upcoming-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline">DEADLINE AUTOPILOT</div>
                <h2 className="font-display text-2xl font-semibold mt-1">Upcoming deadlines</h2>
              </div>
              <Link to="/applications" className="text-sm text-neutral-600 hover:text-neutral-900 flex items-center gap-1">All applications <ArrowRight size={14} /></Link>
            </div>
            {loading ? (
              <div className="text-neutral-500 text-sm">Loading…</div>
            ) : upcoming.length === 0 ? (
              <div className="border border-dashed border-neutral-300 rounded-xl p-8 text-center">
                <div className="text-neutral-700 mb-2">No applications yet.</div>
                <Link to="/grants" className="btn-primary" data-testid="empty-apps-cta">Find your first grant <ArrowRight size={16} /></Link>
              </div>
            ) : (
              <ul className="divide-y divide-neutral-200">
                {upcoming.map((a) => {
                  const d = daysUntil(a.grant_deadline);
                  const urgent = d < 14;
                  return (
                    <li key={a.id} className="py-4 flex items-center justify-between gap-4" data-testid={`upcoming-${a.id}`}>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{a.grant_title}</div>
                        <div className="text-xs text-neutral-500 mt-0.5">{a.status.replace("_", " ")} · deadline {new Date(a.grant_deadline).toLocaleDateString()}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs px-2 py-1 rounded-full border ${urgent ? "border-red-300 bg-red-50 text-red-700" : "border-neutral-200 bg-neutral-50 text-neutral-700"}`}>
                          <Clock size={12} className="inline mr-1" /> {d}d
                        </span>
                        <Link to={`/applications/${a.id}`} className="btn-ghost text-sm">Open</Link>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Recommended grants */}
          <div className="gf-card p-6" data-testid="recommended-panel">
            <div className="overline">RECOMMENDED FOR YOU</div>
            <h2 className="font-display text-2xl font-semibold mt-1 mb-4">Top matches</h2>
            <ul className="space-y-4">
              {featured.slice(0, 4).map((g) => {
                const match = computeMatch(g, user);
                return (
                  <li key={g.id} className="border border-neutral-200 rounded-lg p-4 hover:border-neutral-900 transition-colors" data-testid={`rec-${g.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <Link to={`/grants/${g.id}`} className="font-medium hover:underline block truncate">{g.title}</Link>
                        <div className="text-xs text-neutral-500 mt-1">{g.agency}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-display font-bold text-neutral-900">{match}%</div>
                        <div className="text-[10px] text-neutral-500 uppercase tracking-widest">match</div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between text-xs">
                      <span className="text-neutral-600">{currency(g.amount_min)} – {currency(g.amount_max)}</span>
                      <span className="text-neutral-500">{daysUntil(g.deadline)}d left</span>
                    </div>
                  </li>
                );
              })}
            </ul>
            <Link to="/grants" className="mt-5 btn-ghost w-full justify-center text-sm inline-flex" data-testid="see-all-grants">
              See all 40+ grants <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        {/* CTA banner */}
        <div className="mt-10 rounded-2xl bg-neutral-900 text-white p-8 lg:p-10 grid lg:grid-cols-3 gap-6 items-center" data-testid="ai-cta-banner">
          <div className="lg:col-span-2">
            <div className="overline text-white/70">AI DOC GENERATOR</div>
            <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">Draft a funder-ready document in 30 seconds.</h3>
            <p className="mt-2 text-white/80 max-w-xl">Business plans, project narratives, cover letters — grounded in your organization profile and the specific grant you're chasing.</p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <Link to="/generate" className="bg-white text-neutral-900 rounded-full px-5 py-3 font-medium inline-flex items-center gap-2 hover:bg-neutral-100 transition-colors" data-testid="generate-cta">
              <Sparkles size={16} /> Generate document
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
